/**
 * Typed API Client
 *
 * A fetch-based HTTP client with:
 * - JWT token auto-attachment
 * - Response error handling
 * - Automatic retry for network errors
 * - TypeScript types for all requests/responses
 * - Mock mode for development without backend
 */

import type {
  ApiClientConfig,
  RequestOptions,
  RetryConfig,
} from './types';
import {
  ApiError,
  NetworkError,
  TimeoutError,
  isRetryableError,
  parseErrorResponse,
} from './errors';
import { isMockEnabled, matchMockRoute } from './mockData';

// Default configuration
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const DEFAULT_RETRIES = 3;

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: DEFAULT_RETRIES,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
};

/**
 * Calculate delay for exponential backoff with jitter
 */
function calculateRetryDelay(attempt: number, config: RetryConfig): number {
  // Exponential backoff: delay = baseDelay * 2^attempt
  const exponentialDelay = config.baseDelay * Math.pow(2, attempt);

  // Cap at max delay
  const cappedDelay = Math.min(exponentialDelay, config.maxDelay);

  // Add jitter (random 0-30% of delay)
  const jitter = cappedDelay * Math.random() * 0.3;

  return cappedDelay + jitter;
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Build URL with query parameters
 */
function buildUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  // Remove trailing slash from baseUrl and leading slash from path
  const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
  const cleanPath = path.replace(/^\/+/, '');
  let url = `${cleanBaseUrl}/${cleanPath}`;

  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return url;
}

/**
 * Create a typed API client instance
 */
export function createApiClient(config: ApiClientConfig) {
  const { baseUrl, timeout = DEFAULT_TIMEOUT, retries = DEFAULT_RETRIES, getToken, onUnauthorized } = config;

  /**
   * Make an HTTP request with retry logic
   */
  async function request<T>(
    path: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      method = 'GET',
      body,
      headers: customHeaders = {},
      skipAuth = false,
      timeout: requestTimeout = timeout,
      retries: requestRetries = retries,
      params,
    } = options;

    const url = buildUrl(baseUrl, path, params);
    const retryConfig = { ...DEFAULT_RETRY_CONFIG, maxRetries: requestRetries };

    // Check for mock mode
    if (isMockEnabled()) {
      const mockMatch = matchMockRoute(method, path);
      if (mockMatch) {
        // Simulate network delay for realism
        await sleep(50 + Math.random() * 100);
        const result = mockMatch.handler(body, mockMatch.params);
        return result as T;
      }
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      try {
        // Build headers
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...customHeaders,
        };

        // Attach JWT token if available and not skipped
        if (!skipAuth && getToken) {
          const token = await getToken();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        }

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

        try {
          const response = await fetch(url, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          // Handle non-OK responses
          if (!response.ok) {
            const errorData = await parseErrorResponse(response);
            const error = new ApiError(
              errorData.message,
              response.status,
              errorData.code,
              errorData.details
            );

            // Handle 401 Unauthorized
            if (error.isAuthError() && onUnauthorized) {
              onUnauthorized();
            }

            // Don't retry client errors (4xx) except for 408 and 429
            if (
              error.isClientError() &&
              response.status !== 408 &&
              response.status !== 429
            ) {
              throw error;
            }

            // For retryable errors, store and continue
            lastError = error;
            if (attempt < retryConfig.maxRetries) {
              const delay = calculateRetryDelay(attempt, retryConfig);
              await sleep(delay);
              continue;
            }

            throw error;
          }

          // Parse successful response
          const data = await response.json();
          return data as T;
        } catch (error) {
          clearTimeout(timeoutId);

          // Handle abort (timeout)
          if (error instanceof DOMException && error.name === 'AbortError') {
            throw new TimeoutError(
              `Request timed out after ${requestTimeout}ms`,
              requestTimeout
            );
          }

          throw error;
        }
      } catch (error) {
        // Handle network errors
        if (
          error instanceof TypeError &&
          error.message.includes('fetch')
        ) {
          lastError = new NetworkError('Network request failed', error);
        } else if (error instanceof Error) {
          lastError = error;
        } else {
          lastError = new Error(String(error));
        }

        // Check if we should retry
        if (isRetryableError(lastError) && attempt < retryConfig.maxRetries) {
          const delay = calculateRetryDelay(attempt, retryConfig);
          await sleep(delay);
          continue;
        }

        throw lastError;
      }
    }

    // Should not reach here, but just in case
    throw lastError || new Error('Request failed after retries');
  }

  return {
    /**
     * Make a GET request
     */
    get<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
      return request<T>(path, { ...options, method: 'GET' });
    },

    /**
     * Make a POST request
     */
    post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
      return request<T>(path, { ...options, method: 'POST', body });
    },

    /**
     * Make a PUT request
     */
    put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
      return request<T>(path, { ...options, method: 'PUT', body });
    },

    /**
     * Make a PATCH request
     */
    patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
      return request<T>(path, { ...options, method: 'PATCH', body });
    },

    /**
     * Make a DELETE request
     */
    delete<T>(path: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
      return request<T>(path, { ...options, method: 'DELETE' });
    },

    /**
     * Make a raw request with full options
     */
    request,
  };
}

/**
 * Type for the API client instance
 */
export type ApiClient = ReturnType<typeof createApiClient>;
